/*
  Free gift with purchase (Alex, 19 Oct 2026). Settings live in snippets/oobi-free-gift-config.liquid.
  - Adds the gift (as its own $0 line, marked with the hidden property _oobi_gift) once the rest of the cart reaches the threshold.
  - Takes it out again if the cart drops below the threshold. Keeps it at quantity 1.
  - If the shopper removes it, we don't keep pushing it back in (the bar offers "Add my free apron").
  - Safety: the gift is its own $0 product; if its line ever isn't $0, it's removed and we stop for this visit.
  - Sparkle: when a milestone is newly reached (1 = free shipping, 2 = free gift), the bar gets a short sparkle.
*/
(() => {
  const cfgEl = document.getElementById('oobi-gift-config');
  const store = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { v === null ? sessionStorage.removeItem(k) : sessionStorage.setItem(k, v); } catch (e) {} },
  };
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const onCartPage = () => window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart';

  // ---- 15% Off $150 end guard (26 Oct 2026) --------------------------------------------------------------
  // Liquid drops the 15% stage and the 'more magic' add-ons once the promo ends (snippets/oobi-spend-promo-config).
  // If a page was opened before the end and is still showing them, re-render the cart once; if they're still
  // there after that, hide them, so nothing promises 15% after the discount has stopped.
  let promoRetried = false;
  function promoGuard() {
    const bar = document.querySelector('[data-oobi-promo-ends]');
    if (!bar) return;
    const ends = parseInt(bar.getAttribute('data-oobi-promo-ends'), 10) * 1000;
    if (!ends || Date.now() < ends) return;
    if (!promoRetried) {
      promoRetried = true;
      if (onCartPage()) {
        if (store.get('oobiPromoReloaded') !== String(ends)) { store.set('oobiPromoReloaded', String(ends)); window.location.reload(); return; }
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }
    }
    document.documentElement.classList.add('oobi-promo-over');
  }
  (() => {
    const bar = document.querySelector('[data-oobi-promo-ends]');
    const ends = bar ? parseInt(bar.getAttribute('data-oobi-promo-ends'), 10) * 1000 : 0;
    const wait = ends - Date.now();
    if (wait > 0 && wait < 2147483000) setTimeout(promoGuard, wait + 1000);
  })();

  // ---- Sparkle -------------------------------------------------------------------------------------------
  // The bar (snippets/free-shipping-bar.liquid) carries data-oobi-level = how many milestones are reached.
  let sparkleTimer = null;
  function sparkleWhenReady(level) {
    clearInterval(sparkleTimer);
    let tries = 0;
    sparkleTimer = setInterval(() => {
      tries++;
      const bars = document.querySelectorAll('.oobi-shipping-goal[data-oobi-level="' + level + '"]');
      if (bars.length || tries > 30) {
        clearInterval(sparkleTimer);
        bars.forEach((bar) => {
          bar.classList.remove('oobi-sparkle');
          void bar.offsetWidth; // restart the animation
          bar.classList.add('oobi-sparkle');
          setTimeout(() => bar.classList.remove('oobi-sparkle'), 1600);
        });
      }
    }, 100);
  }
  function checkSparkle(level) {
    const before = parseInt(store.get('oobiGoalLevel') || '0', 10);
    store.set('oobiGoalLevel', String(level));
    if (level > before) sparkleWhenReady(level);
  }
  function levelFromDom() {
    const bar = document.querySelector('.oobi-shipping-goal[data-oobi-level]');
    return bar ? parseInt(bar.getAttribute('data-oobi-level'), 10) : null;
  }

  // ---- Gift ----------------------------------------------------------------------------------------------
  const cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null;
  // Gift switched off for this visit (discount not working): hide the 'add my apron' line in the bar
  const markOff = () => { if (store.get('oobiGiftOff')) document.documentElement.classList.add('oobi-gift-off'); };
  markOff();
  const isGiftLine = (item) => cfg && item.variant_id === cfg.variantId && item.properties && item.properties._oobi_gift;
  const qualifying = (cart) => cart.items.filter((i) => !isGiftLine(i)).reduce((sum, i) => sum + i.final_line_price, 0);

  let busy = false;
  let lastHadGift = null;

  async function post(path, body) {
    const res = await fetch(root + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    if (!res.ok) throw new Error(path + ' ' + res.status);
    return res.json();
  }
  async function getCart() { return (await fetch(root + 'cart.js')).json(); }
  function refresh() {
    if (onCartPage()) { window.location.reload(); return; }
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  async function addGift() {
    await post('cart/add.js', { items: [{ id: cfg.variantId, quantity: 1, properties: { _oobi_gift: '1' } }] });
    const cart = await getCart();
    const line = cart.items.find(isGiftLine);
    if (line && line.final_line_price > 0) {
      // The discount isn't making it free (switched off?): take it back out and stop for this visit.
      await post('cart/change.js', { id: line.key, quantity: 0 });
      store.set('oobiGiftOff', '1');
      markOff();
      console.warn('Oobi free gift: the gift line was not $0, so it was removed. Check the gift product price.');
    }
  }

  async function sync(cart) {
    if (!cfg || busy || store.get('oobiGiftOff')) return;
    const giftLines = cart.items.filter(isGiftLine);
    const line = giftLines[0];
    const qualifies = qualifying(cart) >= cfg.threshold;

    // Shopper took the gift out while still qualifying: respect that until the cart drops below the line.
    if (lastHadGift && !line && qualifies) store.set('oobiGiftDeclined', '1');
    if (!qualifies) store.set('oobiGiftDeclined', null);
    lastHadGift = !!line;

    let changed = false;
    busy = true;
    try {
      if (qualifies && !line && !store.get('oobiGiftDeclined')) {
        await addGift();
        changed = true;
      } else if (!qualifies && line) {
        for (const g of giftLines) await post('cart/change.js', { id: g.key, quantity: 0 });
        changed = true;
      } else if (line && (giftLines.length > 1 || line.quantity > 1)) {
        // Only ever one free apron. Shopify splits extra units into their own paid line (different key), so keep the
        // free line and drop the rest, one change at a time on a fresh cart (keys can change after each change).
        for (let i = 0; i < 4; i++) {
          const lines = (await getCart()).items.filter(isGiftLine);
          if (lines.length <= 1 && (!lines[0] || lines[0].quantity <= 1)) break;
          const keep = lines.find((l) => l.final_line_price === 0) || lines[0];
          const extra = lines.find((l) => l !== keep);
          if (extra) await post('cart/change.js', { id: extra.key, quantity: 0 });
          else await post('cart/change.js', { id: keep.key, quantity: 1 });
        }
        changed = true;
      }
    } catch (e) {
      console.warn('Oobi free gift:', e);
    } finally {
      busy = false;
    }
    if (changed) {
      lastHadGift = qualifies && !store.get('oobiGiftOff');
      refresh();
    }
  }


  // ---- Checkout guard (20 Oct 2026) -----------------------------------------------------------------------
  // Found in a live phone test: tapping Checkout within ~1s of crossing $100 sent the shopper to checkout before
  // the gift was added (or removed), so the apron was missing on a $109.90 order, or free on a $54.95 one.
  // Now a Checkout tap waits for any gift update, puts the gift right on a fresh cart, then goes to checkout.
  // Never blocks for more than ~4s: if anything fails, it goes to checkout anyway.
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  async function fixGiftNow() {
    for (let i = 0; i < 30 && busy; i++) await sleep(100); // let a running sync finish
    await sleep(300); // let the theme's own quantity change land
    const cart = await getCart();
    const giftLines = cart.items.filter(isGiftLine);
    const qualifies = qualifying(cart) >= cfg.threshold;
    if (qualifies && !giftLines.length && !store.get('oobiGiftDeclined')) await addGift();
    else if (!qualifies && giftLines.length) for (const g of giftLines) await post('cart/change.js', { id: g.key, quantity: 0 });
  }
  let leaving = false;
  function guardCheckout(event, go) {
    if (!cfg || store.get('oobiGiftOff') || leaving) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    leaving = true;
    const done = () => go();
    Promise.race([fixGiftNow().catch((e) => console.warn('Oobi free gift:', e)), sleep(4000)]).then(done, done);
  }
  document.addEventListener('click', (event) => {
    const el = event.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]');
    if (!el) return;
    guardCheckout(event, () => { window.location.href = root + 'checkout'; });
  }, true);
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const sub = event.submitter;
    if (!form || !/\/cart/.test(form.getAttribute('action') || '') || !(sub && sub.name === 'checkout')) return;
    guardCheckout(event, () => { window.location.href = root + 'checkout'; });
  }, true);

  // "Add my free apron" button in the bar (shown if the shopper removed it)
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-oobi-gift-add]');
    if (!btn || !cfg) return;
    event.preventDefault();
    store.set('oobiGiftDeclined', null);
    btn.disabled = true;
    try { await addGift(); } catch (e) { console.warn('Oobi free gift:', e); }
    lastHadGift = true;
    refresh();
  });

  document.addEventListener('cart:change', (event) => {
    const cart = event.detail && event.detail.cart;
    if (!cart || !cart.items) return;
    sync(cart);
  });

  // The drawer re-renders after every change: read the milestone level from the fresh bar.
  new MutationObserver(() => {
    promoGuard();
    const level = levelFromDom();
    if (level !== null && String(level) !== store.get('oobiGoalLevel')) checkSparkle(level);
  }).observe(document.documentElement, { childList: true, subtree: true });

  const start = async () => {
    promoGuard();
    const level = levelFromDom();
    // First page of the visit: remember where the cart is, no sparkle for milestones reached earlier
    if (level !== null) store.get('oobiGoalLevel') === null ? store.set('oobiGoalLevel', String(level)) : checkSparkle(level);
    if (!cfg) return;
    try {
      const cart = await getCart();
      lastHadGift = !!cart.items.find(isGiftLine);
      sync(cart);
    } catch (e) {}
  };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
})();
